import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Avatar,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    GridProps,
    Tab,
    Tabs,
    Typography,
    styled,
    useTheme,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiChevronDownCircle,
    mdiChevronUpCircle,
    mdiCloseOctagon,
    mdiMortarPestlePlus,
    mdiPlusCircle,
    mdiVectorPolyline,
} from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
    fetchLanes,
    fetchNetworkRecommendations,
    updateSelectedRecommendations,
    updateSelectedRecommendationType,
} from '../state/features/networkRxSlice';
import {
    LessFreightRecommendation,
    MoreFreightRecommendation,
    Recommendation,
    StartServiceRecommendation,
} from '../interfaces/services/networkRecommendations';
import { ApiStatus, RecommendationType } from '../utils/enums';
import { buildLaneLink } from '../utils/navigationHelpers';
import { formatHealthFilters, parseLaneId } from '../utils/stateHelpers';
import { useFilters } from '../components/Filters/useFilters';
import NetworkHealthImpactCard from '../components/Cards/networkHealthImpactCard';
import NetworkRxMap from '../components/Map/NetworkRxMap';
import MoveMoreFreightTable from '../components/Tables/MoveMoreFreightTable';
import MoveLessFreightTable from '../components/Tables/MoveLessFreightTable';
import StartServiceTable from '../components/Tables/StartServiceTable';
import StopServiceTable from '../components/Tables/StopServiceTable';

interface StyleProps extends GridProps {
    selectedRecommendations: (Recommendation | MoreFreightRecommendation | LessFreightRecommendation | StartServiceRecommendation)[];
}

const classesPrefix = 'networkRx';

const classes = {
    pageRoot: `${classesPrefix}-pageRoot`,
    pageRootItem: `${classesPrefix}-pageRootItem`,
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    tableWrapper: `${classesPrefix}-tableWrapper`,
    mapWrapper: `${classesPrefix}-mapWrapper`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};
const StyledGrid = styled(
    Grid,
    {
        // This prevents passing the StyleProps down to the component
        shouldForwardProp: (prop) => { return prop !== 'selectedRecommendations'; },
    }
)<StyleProps>(({ selectedRecommendations, theme }) => {
    return {
        [`&.${classes.pageRoot}`]: {
            minHeight: '100%',
        },
        [`& .${classes.pageRootItem}`]: {
            overflow: 'auto',
            padding: theme.spacing(2),
        },
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
        [`& .${classes.tableWrapper}`]: {
            marginTop: theme.spacing(1),
            minHeight: '300px',
            height: selectedRecommendations.length === 0 ? 'calc(100vh - 598px)' : 'calc(100vh - 659px)',
        },
        [`& .${classes.mapWrapper}`]: {
            height: 'calc(100vh - 168px)',
            minHeight: '350px',
        },
    };
});

const NetworkRx = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const navigate = useNavigate();
    const filters = useFilters();

    const [hoveredLaneId, setHoveredLaneId] = useState<string | null>(null);

    const selectedRecommendationType = useAppSelector((state) => { return state.networkRx.selectedRecommendationType; });
    const selectedRecommendations = useAppSelector((state) => { return state.networkRx.selectedRecommendations; });

    const networkRecommendationsApiStatus = useAppSelector((state) => { return state.networkRx.networkRecommendationsApiStatus; });
    const networkRecommendations = useAppSelector((state) => { return state.networkRx.networkRecommendations; });

    const lanesApiStatus = useAppSelector((state) => { return state.networkRx.lanesApiStatus; });
    const lanes = useAppSelector((state) => { return state.networkRx.lanes; });

    useEffect(() => {
        dispatch(fetchNetworkRecommendations());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchLanes());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(updateSelectedRecommendations([]));
        };
    }, [dispatch]);

    const healthFilters = formatHealthFilters();

    const handleRecommendationTabChange = (event: React.SyntheticEvent<Element, Event>, newTab: RecommendationType): void => {
        dispatch(updateSelectedRecommendationType(newTab));
    };

    const handleHoveredLaneChange = (laneId: string | null): void => {
        setHoveredLaneId(laneId);
    };

    const handleLaneClick = (laneId: string): void => {
        const { origin, destination } = parseLaneId(laneId);
        navigate(buildLaneLink(origin, destination, {
            ...filters,
            earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
            latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
        }));
    };

    return (
        <StyledGrid
            container
            className={classes.pageRoot}
            justifyContent='flex-start'
            direction='column'
            selectedRecommendations={selectedRecommendations}
        >
            <Grid item xs className={classes.pageRootItem}>
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={7}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <NetworkHealthImpactCard />
                            </Grid>

                            <Grid item xs={12}>
                                <Card>
                                    <CardHeader
                                        classes={{
                                            content: classes.cardHeaderContent,
                                        }}
                                        avatar={
                                            <Avatar>
                                                <Icon path={mdiMortarPestlePlus} size={1} />
                                            </Avatar>
                                        }
                                        title='Recommendations'
                                        titleTypographyProps={{
                                            variant: 'h5',
                                        }}
                                    />

                                    <CardContent className={classes.cardContent}>
                                        <Tabs
                                            indicatorColor='primary'
                                            variant='scrollable'
                                            scrollButtons='auto'
                                            value={selectedRecommendationType}
                                            onChange={handleRecommendationTabChange}
                                        >
                                            <Tab
                                                value={RecommendationType.MoveMoreFreight}
                                                label={
                                                    <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Grid item>
                                                            <Icon
                                                                path={mdiChevronUpCircle}
                                                                size={1}
                                                                color={selectedRecommendationType === RecommendationType.MoveMoreFreight ? theme.palette.success.main : theme.palette.text.secondary}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant='inherit'>Move More Freight</Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Chip
                                                                label={networkRecommendations.recommendation_count_details.move_more_freight}
                                                                size='small'
                                                                color={selectedRecommendationType === RecommendationType.MoveMoreFreight ? 'primary' : 'default'}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                }
                                            />
                                            <Tab
                                                value={RecommendationType.MoveLessFreight}
                                                label={
                                                    <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Grid item>
                                                            <Icon
                                                                path={mdiChevronDownCircle}
                                                                size={1}
                                                                color={selectedRecommendationType === RecommendationType.MoveLessFreight ? theme.palette.warning.dark : theme.palette.text.secondary}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant='inherit'>Move Less Freight</Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Chip
                                                                label={networkRecommendations.recommendation_count_details.move_less_freight}
                                                                size='small'
                                                                color={selectedRecommendationType === RecommendationType.MoveLessFreight ? 'primary' : 'default'}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                }
                                            />
                                            <Tab
                                                value={RecommendationType.StartService}
                                                label={
                                                    <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Grid item>
                                                            <Icon
                                                                path={mdiPlusCircle}
                                                                size={1}
                                                                color={selectedRecommendationType === RecommendationType.StartService ? theme.palette.info.main : theme.palette.text.secondary}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant='inherit'>Start Service</Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Chip
                                                                label={networkRecommendations.recommendation_count_details.start_service}
                                                                size='small'
                                                                color={selectedRecommendationType === RecommendationType.StartService ? 'primary' : 'default'}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                }
                                            />
                                            <Tab
                                                value={RecommendationType.StopService}
                                                label={
                                                    <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Grid item>
                                                            <Icon
                                                                path={mdiCloseOctagon}
                                                                size={1}
                                                                color={selectedRecommendationType === RecommendationType.StopService ? theme.palette.error.main : theme.palette.text.secondary}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant='inherit'>Stop Service</Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Chip
                                                                label={networkRecommendations.recommendation_count_details.stop_service}
                                                                size='small'
                                                                color={selectedRecommendationType === RecommendationType.StopService ? 'primary' : 'default'}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                }
                                            />
                                        </Tabs>

                                        <div className={classes.tableWrapper}>
                                            {
                                                selectedRecommendationType === RecommendationType.MoveMoreFreight &&
                                                <MoveMoreFreightTable />
                                            }
                                            {
                                                selectedRecommendationType === RecommendationType.MoveLessFreight &&
                                                <MoveLessFreightTable />
                                            }
                                            {
                                                selectedRecommendationType === RecommendationType.StartService &&
                                                <StartServiceTable />
                                            }
                                            {
                                                selectedRecommendationType === RecommendationType.StopService &&
                                                <StopServiceTable />
                                            }
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <Card>
                            <CardHeader
                                classes={{
                                    content: classes.cardHeaderContent,
                                }}
                                avatar={
                                    <Avatar>
                                        <Icon path={mdiVectorPolyline} size={1} />
                                    </Avatar>
                                }
                                title='Recommended Lanes'
                                titleTypographyProps={{
                                    variant: 'h5',
                                }}
                            />

                            <CardContent className={`${classes.cardContent} ${classes.mapWrapper}`}>
                                <NetworkRxMap
                                    isFetchingData={
                                        networkRecommendationsApiStatus === ApiStatus.Idle || networkRecommendationsApiStatus === ApiStatus.Loading ||
                                        lanesApiStatus === ApiStatus.Idle || lanesApiStatus === ApiStatus.Loading
                                    }
                                    selectedRecommendationType={selectedRecommendationType}
                                    selectedRecommendations={selectedRecommendations}
                                    networkRecommendations={networkRecommendations}
                                    lanes={lanes}
                                    hoveredLaneId={hoveredLaneId}
                                    handleHoveredLaneChange={handleHoveredLaneChange}
                                    handleLaneClick={handleLaneClick}
                                    theme={theme}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default NetworkRx;
